import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/dateFormat';

interface SecurityAlert {
  id: number;
  alert_type: string;
  severity: string;
  title: string;
  description: string;
  details: Record<string, any>;
  status: string;
  action_taken: string | null;
  created_at: string;
  acknowledged_at: string | null;
  share_id: number | null;
  shared_with_email: string | null;
  filename: string | null;
  user_email: string | null;
}

interface AlertSummary {
  open: number;
  acknowledged: number;
  resolved: number;
  high_open: number;
  critical_open: number;
  last_24h: number;
}

const SEVERITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  low: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
};

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-red-100 text-red-700',
  acknowledged: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
};

// Security settings stored in localStorage
interface SecuritySettings {
  passwordMinLength: number;
  requireUppercase: boolean;
  requireNumber: boolean;
  sessionTimeout: string;
  twoFactor: boolean;
}

const DEFAULT_SECURITY_SETTINGS: SecuritySettings = {
  passwordMinLength: 8,
  requireUppercase: true,
  requireNumber: true,
  sessionTimeout: '8h',
  twoFactor: false,
};

const SESSION_TIMEOUT_OPTIONS = [
  { value: '1h', label: '1 hour' },
  { value: '4h', label: '4 hours' },
  { value: '8h', label: '8 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
];

const SecuritySettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<SecuritySettings>(() => {
    try {
      const saved = localStorage.getItem('discoverant_security_settings');
      return saved ? { ...DEFAULT_SECURITY_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SECURITY_SETTINGS;
    } catch { return DEFAULT_SECURITY_SETTINGS; }
  });
  const [saved, setSaved] = useState(false);

  const update = (key: keyof SecuritySettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem('discoverant_security_settings', JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="rounded-xl border p-5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
      <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Security Settings</h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Password minimum length</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Minimum characters required for user passwords</div>
          </div>
          <input
            type="number"
            min={6}
            max={32}
            value={settings.passwordMinLength}
            onChange={e => update('passwordMinLength', parseInt(e.target.value) || 8)}
            className="w-20 px-3 py-1.5 text-sm rounded-lg border text-center"
            style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Require uppercase letter</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Passwords must contain at least one uppercase letter</div>
          </div>
          <button
            onClick={() => update('requireUppercase', !settings.requireUppercase)}
            className="relative w-10 h-5 rounded-full transition-colors"
            style={{ backgroundColor: settings.requireUppercase ? '#0D9488' : '#d1d5db' }}
          >
            <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform" style={{ transform: settings.requireUppercase ? 'translateX(20px)' : 'translateX(0)' }} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Require number</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Passwords must contain at least one digit</div>
          </div>
          <button
            onClick={() => update('requireNumber', !settings.requireNumber)}
            className="relative w-10 h-5 rounded-full transition-colors"
            style={{ backgroundColor: settings.requireNumber ? '#0D9488' : '#d1d5db' }}
          >
            <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform" style={{ transform: settings.requireNumber ? 'translateX(20px)' : 'translateX(0)' }} />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Session timeout</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Automatically log out inactive users after this period</div>
          </div>
          <select
            value={settings.sessionTimeout}
            onChange={e => update('sessionTimeout', e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border"
            style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
          >
            {SESSION_TIMEOUT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Two-factor authentication</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Require 2FA for all users — Coming soon</div>
          </div>
          <button
            disabled
            title="Two-factor authentication is coming soon"
            className="relative w-10 h-5 rounded-full"
            style={{ backgroundColor: '#d1d5db', opacity: 0.5, cursor: 'not-allowed' }}
          >
            <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full" style={{ transform: 'translateX(0)' }} />
          </button>
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#0D9488' }}
          >
            Save Settings
          </button>
          {saved && <span className="text-xs font-medium" style={{ color: '#16a34a' }}>Saved</span>}
        </div>
      </div>
    </div>
  );
};

const SecurityAlertsPage: React.FC = () => {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '50');

      const [alertsRes, summaryRes] = await Promise.all([
        fetch(`/api/v1/security-alerts?${params}`, { headers }),
        fetch('/api/v1/security-alerts/summary', { headers }),
      ]);

      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data.alerts || []);
      }
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
    setLoading(false);
  }, [statusFilter, token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleAction = async (alertId: number, action: string) => {
    setActionLoading(alertId);
    try {
      const endpoint = action === 'acknowledge'
        ? `/api/v1/security-alerts/${alertId}/acknowledge`
        : `/api/v1/security-alerts/${alertId}/resolve?action=${action}`;

      const res = await fetch(endpoint, { method: 'POST', headers });
      if (res.ok) {
        await fetchAlerts();
      }
    } catch (err) {
      console.error('Action failed:', err);
    }
    setActionLoading(null);
  };

  return (
    <div className="space-y-6">
      {/* Security Settings */}
      <SecuritySettingsPanel />

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <p className="text-xs text-red-600 font-medium uppercase">Open Alerts</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{summary.open}</p>
            {summary.critical_open > 0 && (
              <p className="text-xs text-red-500 mt-1">{summary.critical_open} critical</p>
            )}
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-xs text-amber-600 font-medium uppercase">Acknowledged</p>
            <p className="text-2xl font-bold text-amber-700 mt-1">{summary.acknowledged}</p>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-xl p-4">
            <p className="text-xs text-green-600 font-medium uppercase">Resolved</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{summary.resolved}</p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-xs text-blue-600 font-medium uppercase">Last 24h</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{summary.last_24h}</p>
          </div>
        </div>
      )}

      {/* Explanation banner — collapsible */}
      <details className="rounded-xl border border-blue-100 bg-blue-50/50">
        <summary className="px-4 py-2.5 text-xs font-medium text-blue-700 cursor-pointer select-none hover:bg-blue-50">
          About security monitoring
        </summary>
        <div className="px-4 pb-3 text-xs text-blue-600 leading-relaxed space-y-1.5">
          <p>Security monitoring tracks suspicious activity across the platform. Alerts are generated automatically when potential threats are detected:</p>
          <ul className="list-disc ml-4 space-y-0.5">
            <li><strong>Failed login attempts</strong> — repeated unsuccessful logins from the same IP</li>
            <li><strong>File scan results</strong> — uploads flagged by virus scanning</li>
            <li><strong>Integrity checks</strong> — file checksums that don't match on download</li>
            <li><strong>Domain verification</strong> — suspicious sharing domains detected</li>
            <li><strong>Access violations</strong> — unauthorised API access attempts</li>
            <li><strong>Rate limiting</strong> — unusual request volumes from a single source</li>
          </ul>
          <p>Alerts require acknowledgement from an administrator. Unresolved alerts are highlighted in the dashboard.</p>
        </div>
      </details>

      {/* Filter pills */}
      <div className="flex items-center gap-2">
        {['open', 'acknowledged', 'resolved'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Alert list */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-3">
            <svg className="w-12 h-12 mx-auto text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">No {statusFilter} alerts</p>
          <p className="text-gray-400 text-sm mt-1">Security monitoring is active</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const sev = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.medium;
            return (
              <div
                key={alert.id}
                className={`${sev.bg} border ${sev.border} rounded-xl p-4`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${sev.text} ${sev.bg}`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[alert.status] || ''}`}>
                        {alert.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {alert.alert_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">{alert.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      {alert.created_at && <span>{formatDateTime(alert.created_at)}</span>}
                      {alert.shared_with_email && <span>Recipient: {alert.shared_with_email}</span>}
                      {alert.filename && <span>File: {alert.filename}</span>}
                      {alert.user_email && <span>User: {alert.user_email}</span>}
                    </div>
                  </div>

                  {/* Action buttons */}
                  {alert.status === 'open' && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleAction(alert.id, 'acknowledge')}
                        disabled={actionLoading === alert.id}
                        className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
                      >
                        Acknowledge
                      </button>
                      <button
                        onClick={() => handleAction(alert.id, 'revoke_share')}
                        disabled={actionLoading === alert.id}
                        className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Revoke Share
                      </button>
                    </div>
                  )}
                  {alert.status === 'acknowledged' && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleAction(alert.id, 'dismiss')}
                        disabled={actionLoading === alert.id}
                        className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => handleAction(alert.id, 'revoke_share')}
                        disabled={actionLoading === alert.id}
                        className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Revoke Share
                      </button>
                      <button
                        onClick={() => handleAction(alert.id, 'block_user')}
                        disabled={actionLoading === alert.id}
                        className="px-3 py-1.5 text-xs font-medium bg-gray-800 text-white rounded-lg hover:bg-gray-900"
                      >
                        Block User
                      </button>
                    </div>
                  )}
                  {alert.status === 'resolved' && alert.action_taken && (
                    <span className="text-xs text-gray-400 ml-4">
                      Action: {alert.action_taken.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SecurityAlertsPage;
